import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getNextPracticeSentence } from "../../courses";
import PracticeSentenceModal from "./PracticeSentenceModal";
import TextareaListen from "../Pronunciation/TextareaListen";

// difficulty: 1 = easy, 2 = intermediate, 3 = hard
const DIFFICULTY_BADGE_STYLES = {
    1: "bg-green-100 text-green-800",
    2: "bg-yellow-100 text-yellow-800",
    3: "bg-red-100 text-red-800",
};

const NextSentenceCard = ({ selectedLanguage, onPracticed }) => {
    const { t } = useTranslation();
    const [sentence, setSentence] = useState(null);
    const [difficulty, setDifficulty] = useState(null);
    // Set when the sentence comes from a joined course (adaptive homework).
    const [courseInfo, setCourseInfo] = useState(null);
    const [showPracticeModal, setShowPracticeModal] = useState(false);
    // Bumped for every in-modal "next sentence" so the modal remounts with a
    // clean practice state (a key on the sentence alone could repeat).
    const [practiceRound, setPracticeRound] = useState(0);
    const [currentlyListening, setCurrentlyListening] = useState(false);

    const applyResult = useCallback((result) => {
        setSentence(result.sentence);
        setDifficulty(result.difficulty);
        setCourseInfo(
            result.source === "course"
                ? { title: result.course_title, folder: result.folder }
                : null
        );
    }, []);

    const loadSentence = useCallback(() => {
        setSentence(null);
        setDifficulty(null);
        setCourseInfo(null);
        getNextPracticeSentence(selectedLanguage)
            .then(applyResult)
            .catch((error) => {
                console.error("Failed to load next sentence:", error);
            });
    }, [selectedLanguage, applyResult]);

    useEffect(() => {
        if (!selectedLanguage) return;
        loadSentence();
    }, [selectedLanguage, loadSentence]);

    const openPractice = () => {
        if (sentence) setShowPracticeModal(true);
    };

    // Unlike loadSentence, keeps the current sentence visible until the new
    // one has arrived (the open modal shouldn't fall back to a skeleton).
    const handleNextSentence = useCallback(() => {
        return getNextPracticeSentence(selectedLanguage).then((result) => {
            applyResult(result);
            setPracticeRound((round) => round + 1);
        });
    }, [selectedLanguage, applyResult]);

    return (
        <>
            <div
                className="rounded-lg border border-border bg-secondary p-4 md:p-6 cursor-pointer hover:bg-gray-100 transition-colors"
                role="button"
                tabIndex={0}
                onClick={openPractice}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPractice();
                    }
                }}
            >
                <div className="flex items-center gap-3 md:gap-6">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{t("nextSentence")}</h3>
                            {DIFFICULTY_BADGE_STYLES[difficulty] && (
                                <span
                                    className={`rounded px-2 py-0.5 text-xs font-medium ${DIFFICULTY_BADGE_STYLES[difficulty]}`}
                                >
                                    {t(`difficulty.${difficulty}`)}
                                </span>
                            )}
                            {courseInfo && (
                                <span className="rounded px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800">
                                    {courseInfo.title}
                                    {courseInfo.folder ? ` · ${courseInfo.folder}` : ""}
                                </span>
                            )}
                        </div>

                        {sentence ? (
                            <>
                                <p className="mt-3 text-lg md:text-2xl font-semibold text-gray-900">
                                    &ldquo;{sentence}&rdquo;
                                </p>
                                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                <div className="mt-3 inline-block" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    <TextareaListen
                                        text={sentence}
                                        selectedLanguage={selectedLanguage}
                                        currentlyListening={currentlyListening}
                                        setCurrentlyListening={setCurrentlyListening}
                                        setTextareaHasError={() => {}}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="mt-4 h-6 w-3/4 animate-pulse rounded bg-gray-200" />
                        )}
                    </div>

                    {/* Mic button (visual entry point, opens the practice popup) */}
                    <div className="shrink-0 flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary text-white shadow-[0_0_10px_1px_rgba(128,90,213,0.6)]">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="size-6 md:size-7"
                        >
                            <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5Z" />
                            <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5Z" />
                        </svg>
                    </div>
                </div>
            </div>

            {showPracticeModal && sentence && (
                <PracticeSentenceModal
                    key={practiceRound}
                    sentence={sentence}
                    selectedLanguage={selectedLanguage}
                    onNextSentence={handleNextSentence}
                    onClose={(practiced) => {
                        setShowPracticeModal(false);
                        // Once the sentence was practiced, queue up the next one
                        // and refresh the dashboard stat cards.
                        if (practiced) {
                            loadSentence();
                            onPracticed?.();
                        }
                    }}
                />
            )}
        </>
    );
};

export default NextSentenceCard;
